import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Trash2, Mail, Phone, Home, Euro,
  MapPin, Calendar, ShieldCheck, MessageCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";

const TYPE_LABEL = { sale: "Verkoop", mortgage: "Hypotheek" };
const QUALITY_LABEL = { hot: "Hot", warm: "Warm", cold: "Cold" };
const QUALITY_STYLE = {
  hot: "bg-danger/10 text-danger",
  warm: "bg-amber-100 text-amber-700",
  cold: "bg-slate-100 text-slate-500",
};
const STATUSES = [
  ["new", "Nieuw"], ["reviewed", "Gecontroleerd"], ["qualified", "Gekwalificeerd"],
  ["offered", "Aangeboden"], ["sold", "Verkocht"], ["contacted", "Gecontacteerd"],
  ["appointment", "Afspraak gepland"], ["converted", "Geconverteerd"], ["rejected", "Afgekeurd"],
];

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 border-b border-border/40 py-2.5">
    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
      <Icon size={14} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-navy">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  </div>
);

const AdminLeadDetail = () => {
  const { id } = useParams();
  useSeo({ title: "Lead · Admin", description: "Lead detail" });
  const [lead, setLead] = useState(null);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setErr(""); setLead(null);
    api(`/admin/vastgoed-leads/${id}`).then((d) => {
      setLead(d);
      setStatus(d.status || "new");
      setNotes(d.internal_notes || "");
    }).catch((e) => setErr(e.message));
  };
  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const d = await api(`/admin/vastgoed-leads/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status, internal_notes: notes }),
      });
      setLead(d);
      toast.success("Opgeslagen");
    } catch (e) {
      toast.error(e.message || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Deze lead permanent verwijderen?")) return;
    setBusy(true);
    try {
      await api(`/admin/vastgoed-leads/${id}`, { method: "DELETE" });
      toast.success("Verwijderd");
      window.location.href = "/admin/vastgoed-leads";
    } catch (e) {
      toast.error(e.message || "Verwijderen mislukt");
      setBusy(false);
    }
  };

  if (err) return (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <p className="text-slate-500">{err}</p>
      <Button className="mt-4 rounded-xl" onClick={load}>Opnieuw</Button>
    </div>
  );
  if (!lead) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" size={32} /></div>;

  const addr = [lead.postal_code, lead.house_number, lead.city].filter(Boolean).join(" ");

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/admin/vastgoed-leads" className="inline-flex items-center gap-1 hover:text-navy" data-testid="lead-back"><ArrowLeft size={14} /> Vastgoedleads</Link>
      </div>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="lead-detail-name">{lead.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${QUALITY_STYLE[lead.quality] || QUALITY_STYLE.cold}`}>{QUALITY_LABEL[lead.quality] || lead.quality}</span>
            <span className="rounded-full bg-brandblue-light px-2 py-0.5 text-xs font-semibold text-brandblue">{TYPE_LABEL[lead.lead_type] || lead.lead_type}</span>
            <span className="text-xs text-slate-400">Lead-ID {lead.id.slice(0, 8)}…</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={del} className="text-danger" disabled={busy} data-testid="lead-delete">
          <Trash2 size={14} className="mr-1" /> Verwijderen
        </Button>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Left — persoonsgegevens + woning */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
            <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">Persoonsgegevens</h3>
            <div className="mt-3">
              <Row icon={Mail} label="E-mail" value={<a href={`mailto:${lead.email}`} className="text-brandblue hover:underline">{lead.email}</a>} />
              <Row icon={Phone} label="Telefoon" value={lead.phone ? <a href={`tel:${lead.phone}`} className="text-brandblue hover:underline">{lead.phone}</a> : null} />
              <Row icon={MessageCircle} label="Contactvoorkeur" value={lead.contact_preference} />
              <Row icon={MapPin} label="Adres" value={addr} />
            </div>
          </div>

          {lead.lead_type === "sale" && (
            <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
              <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">Woninggegevens</h3>
              <div className="mt-3">
                <Row icon={Home} label="Type woning" value={lead.property_type} />
                <Row icon={Euro} label="Geschatte waarde" value={lead.estimated_value ? `€${lead.estimated_value.toLocaleString("nl-NL")}` : null} />
                <Row icon={Calendar} label="Verkooptermijn" value={lead.selling_timeline} />
                <Row icon={MessageCircle} label="Reden" value={lead.reason} />
                <Row icon={ShieldCheck} label="Heeft makelaar?" value={lead.has_broker === true ? "Ja" : lead.has_broker === false ? "Nee" : null} />
                <Row icon={ShieldCheck} label="Wil waardebepaling?" value={lead.wants_valuation === true ? "Ja" : lead.wants_valuation === false ? "Nee" : null} />
              </div>
            </div>
          )}

          {lead.lead_type === "mortgage" && (
            <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
              <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">Hypotheek-intentie</h3>
              <div className="mt-3">
                <Row icon={Euro} label="Bruto jaarinkomen" value={lead.income_bracket} />
                <Row icon={Euro} label="Gewenst hypotheekbedrag" value={lead.mortgage_amount ? `€${lead.mortgage_amount.toLocaleString("nl-NL")}` : null} />
                <Row icon={ShieldCheck} label="Starter op de woningmarkt?" value={lead.first_time_buyer === true ? "Ja" : lead.first_time_buyer === false ? "Nee" : null} />
              </div>
            </div>
          )}

          {lead.notes && (
            <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
              <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">Toelichting gebruiker</h3>
              <p className="mt-2 text-sm text-slate-700">{lead.notes}</p>
            </div>
          )}
        </div>

        {/* Right — status + consent + source */}
        <div className="space-y-6">
          <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
            <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">Status</h3>
            <select
              className="mt-3 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="lead-status-select"
            >
              {STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <h4 className="mt-4 text-xs font-semibold text-slate-500">Interne notities</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              className="mt-1 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm"
              placeholder="Alleen zichtbaar voor admins…"
              data-testid="lead-notes"
            />
            <Button onClick={save} disabled={busy} className="mt-3 w-full rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="lead-save">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-1.5" /> Opslaan</>}
            </Button>
          </div>

          <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
            <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">Toestemming</h3>
            <div className="mt-3 text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className={lead.consent_partners ? "text-success" : "text-slate-300"} />
                <span>{lead.consent_partners ? "Verleend" : "Niet verleend"}</span>
              </div>
              {lead.consent_at && <div className="mt-1 text-xs text-slate-400">op {new Date(lead.consent_at).toLocaleString("nl-NL")}</div>}
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
            <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">Bron</h3>
            <div className="mt-3 space-y-1 text-xs text-slate-500">
              <div>Pagina: <span className="font-mono text-slate-700">{lead.source_screen || "direct"}</span></div>
              {lead.utm_source && <div>UTM source: <span className="font-mono">{lead.utm_source}</span></div>}
              {lead.utm_medium && <div>UTM medium: <span className="font-mono">{lead.utm_medium}</span></div>}
              {lead.utm_campaign && <div>UTM campagne: <span className="font-mono">{lead.utm_campaign}</span></div>}
              <div className="mt-2 text-slate-400">Aangemaakt: {new Date(lead.created_at).toLocaleString("nl-NL")}</div>
              <div className="text-slate-400">Bijgewerkt: {new Date(lead.updated_at).toLocaleString("nl-NL")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLeadDetail;
